"use client";

import { useId } from "react";
import { SegmentedControl } from "@/components/ui/segmented-control";

export type FulfillmentType = "delivery" | "pickup";

export interface PickupLocality {
  city: string;
  state: string;
  postalCode: string;
}

const PICKUP_UNAVAILABLE_MESSAGE =
  "Pickup is unavailable for this listing.";

export function formatPickupLocality(locality: PickupLocality) {
  return `${locality.city}, ${locality.state} ${locality.postalCode}`;
}

function pickupNote(
  value: FulfillmentType,
  pickupLocality: PickupLocality | null,
  sourcedToOrder: boolean,
): string | null {
  if (pickupLocality === null) return PICKUP_UNAVAILABLE_MESSAGE;
  const locality = formatPickupLocality(pickupLocality);
  if (sourcedToOrder) {
    return value === "pickup" ? `Pickup near ${locality}, confirmed with your quote.` : null;
  }
  return value === "pickup"
    ? `Pickup from ${locality}.`
    : `Pickup available from ${locality}.`;
}

export function ListingQuoteFulfillmentField({
  value,
  onChange,
  pickupLocality,
  sourcedToOrder = false,
  error,
}: {
  value: FulfillmentType;
  onChange: (next: FulfillmentType) => void;
  pickupLocality: PickupLocality | null;
  sourcedToOrder?: boolean;
  error?: string;
}) {
  const pickupAvailable = pickupLocality !== null;
  const note = pickupNote(value, pickupLocality, sourcedToOrder);
  const pickupNoteId = useId();
  const errorId = useId();
  return (
    <div>
      <SegmentedControl
        fullWidth
        aria-label="Fulfillment method"
        aria-describedby={[note && pickupNoteId, error && errorId].filter(Boolean).join(" ") || undefined}
        value={value}
        onValueChange={onChange}
        options={[
          { value: "delivery", label: "Delivery" },
          {
            value: "pickup",
            label: "Pickup",
            disabled: !pickupAvailable,
            title: pickupAvailable ? undefined : PICKUP_UNAVAILABLE_MESSAGE,
          },
        ]}
      />
      {note ? (
        <p id={pickupNoteId} className="mt-1.5 text-xs text-muted-foreground">
          {note}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="mt-1 text-xs text-destructive-strong">
          {error}
        </p>
      ) : null}
    </div>
  );
}
